import { Heart, MessageCircle, Send, Bookmark, BarChart2, Repeat2, Globe, MoreHorizontal, ThumbsUp } from "lucide-react";

type Platform = "ig_reel" | "ig_feed" | "ig_story" | "fb_feed" | "x_post" | "li_post" | "google_ad";

interface SocialPreviewMockupProps {
  imageUrl?: string;
  caption?: string;
  platform?: Platform;
  hashtags?: string[];
  headline?: string;
  username?: string;
  showPlatformToggle?: boolean;
  onPlatformChange?: (p: Platform) => void;
}

const PLATFORM_LABELS: Record<Platform, string> = {
  ig_reel: "IG Reel",
  ig_feed: "IG Feed",
  ig_story: "IG Story",
  fb_feed: "FB Feed",
  x_post: "X Post",
  li_post: "LinkedIn",
  google_ad: "Google Ad",
};

function Avatar({ name }: { name: string }) {
  const letter = (name || "B")[0].toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
      {letter}
    </div>
  );
}

function IgReelPreview({ imageUrl, caption, hashtags, username }: { imageUrl?: string; caption?: string; hashtags?: string[]; username?: string }) {
  const displayName = username || "yourbrand";
  const shortCaption = caption ? caption.slice(0, 80) : "Your caption here...";
  const tags = (hashtags || []).slice(0, 4);

  return (
    <div
      className="relative w-full overflow-hidden rounded-[1.5rem] bg-black"
      style={{ aspectRatio: "9/16" }}
      data-testid="ig-reel-preview"
    >
      {imageUrl ? (
        <img src={imageUrl} alt="Preview" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-pink-900 to-black" />
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />

      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-3 py-2.5">
        <span className="text-white/0 text-xs">●●●</span>
        <span className="text-white text-[11px] font-semibold tracking-wide">Reels</span>
        <Send size={13} className="text-white" />
      </div>

      <div className="absolute right-2.5 bottom-20 flex flex-col items-center gap-4">
        <div className="flex flex-col items-center gap-0.5">
          <Heart size={22} className="text-white" strokeWidth={2} />
          <span className="text-white text-[9px] font-medium">1.2k</span>
        </div>
        <div className="flex flex-col items-center gap-0.5">
          <MessageCircle size={22} className="text-white" strokeWidth={2} />
          <span className="text-white text-[9px] font-medium">48</span>
        </div>
        <div className="flex flex-col items-center gap-0.5">
          <Send size={22} className="text-white" strokeWidth={2} />
          <span className="text-white text-[9px] font-medium">Share</span>
        </div>
        <Bookmark size={22} className="text-white" strokeWidth={2} />
      </div>

      <div className="absolute bottom-5 left-3 right-12 space-y-1.5">
        <div className="flex items-center gap-2">
          <Avatar name={displayName} />
          <span className="text-white text-[11px] font-semibold">{displayName}</span>
          <span className="text-white/60 text-[10px] border border-white/40 rounded px-1 py-0.5">Follow</span>
        </div>
        <p className="text-white text-[10px] leading-tight line-clamp-2">{shortCaption}</p>
        {tags.length > 0 && (
          <p className="text-purple-300 text-[9px] leading-tight">
            {tags.map(t => `#${t.replace(/^#/, "")}`).join(" ")}
          </p>
        )}
      </div>

      <div className="absolute bottom-2 left-3 right-3">
        <div className="h-[2px] rounded-full bg-white/20">
          <div className="h-full w-[60%] rounded-full bg-white" />
        </div>
      </div>
    </div>
  );
}

function IgFeedPreview({ imageUrl, caption, hashtags, username }: { imageUrl?: string; caption?: string; hashtags?: string[]; username?: string }) {
  const displayName = username || "yourbrand";
  return (
    <div className="w-full rounded-[1rem] bg-[#1a1a1a] border border-white/10 overflow-hidden" data-testid="ig-feed-preview">
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <Avatar name={displayName} />
          <div>
            <p className="text-white text-[11px] font-semibold">{displayName}</p>
            <p className="text-white/40 text-[9px]">Sponsored</p>
          </div>
        </div>
        <MoreHorizontal size={14} className="text-white/50" />
      </div>
      <div className="w-full aspect-square bg-black">
        {imageUrl ? (
          <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-purple-900 to-pink-900" />
        )}
      </div>
      <div className="px-3 py-2 space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Heart size={18} className="text-white" />
            <MessageCircle size={18} className="text-white" />
            <Send size={18} className="text-white" />
          </div>
          <Bookmark size={18} className="text-white" />
        </div>
        <p className="text-white text-[10px] font-semibold">847 likes</p>
        {caption && <p className="text-white text-[10px] leading-snug line-clamp-2"><span className="font-semibold">{displayName}</span> {caption.slice(0, 80)}</p>}
        {(hashtags || []).length > 0 && <p className="text-blue-400 text-[9px]">{(hashtags || []).slice(0, 3).map(t => `#${t.replace(/^#/, "")}`).join(" ")}</p>}
      </div>
    </div>
  );
}

function FbFeedPreview({ imageUrl, caption, username }: { imageUrl?: string; caption?: string; username?: string }) {
  const displayName = username || "Your Brand";
  return (
    <div className="w-full rounded-[1rem] bg-[#1c1e21] border border-white/10 overflow-hidden" data-testid="fb-feed-preview">
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Avatar name={displayName} />
          <div>
            <p className="text-white text-[11px] font-semibold">{displayName}</p>
            <div className="flex items-center gap-1 text-white/40">
              <span className="text-[9px]">Just now ·</span>
              <Globe size={8} />
            </div>
          </div>
        </div>
        <MoreHorizontal size={14} className="text-white/50" />
      </div>
      {caption && <p className="text-white text-[10px] px-3 pb-2 leading-snug">{caption.slice(0, 100)}</p>}
      <div className="w-full" style={{ aspectRatio: "4/3" }}>
        {imageUrl ? (
          <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-blue-900 to-indigo-900" />
        )}
      </div>
      <div className="px-3 py-2 space-y-1.5">
        <div className="flex items-center gap-1 text-white/40 text-[9px]">
          <span>👍❤️😮</span>
          <span>1.4k reactions · 58 comments</span>
        </div>
        <div className="flex items-center justify-around border-t border-white/10 pt-1.5">
          {["👍 Like", "💬 Comment", "↗ Share"].map(label => (
            <button key={label} className="text-white/50 text-[10px] font-medium">{label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function XPostPreview({ imageUrl, caption, hashtags, username }: { imageUrl?: string; caption?: string; hashtags?: string[]; username?: string }) {
  const displayName = username || "YourBrand";
  const handle = `@${displayName.toLowerCase().replace(/\s+/g, "")}`;
  return (
    <div className="w-full rounded-[1rem] bg-black border border-white/10 p-3 space-y-2" data-testid="x-post-preview">
      <div className="flex items-start gap-2">
        <Avatar name={displayName} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-white text-[11px] font-bold">{displayName}</span>
            <span className="text-white/40 text-[10px]">{handle}</span>
            <span className="text-white/30 text-[10px]">· now</span>
          </div>
          {caption && <p className="text-white text-[10px] mt-1 leading-snug">{caption.slice(0, 120)}</p>}
          {(hashtags || []).length > 0 && <p className="text-blue-400 text-[9px] mt-0.5">{(hashtags || []).slice(0, 3).map(t => `#${t.replace(/^#/, "")}`).join(" ")}</p>}
        </div>
      </div>
      {imageUrl && (
        <div className="rounded-xl overflow-hidden" style={{ aspectRatio: "16/9" }}>
          <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="flex items-center justify-between text-white/40 pt-1">
        <div className="flex items-center gap-1"><MessageCircle size={13} /><span className="text-[9px]">24</span></div>
        <div className="flex items-center gap-1"><Repeat2 size={13} /><span className="text-[9px]">48</span></div>
        <div className="flex items-center gap-1"><Heart size={13} /><span className="text-[9px]">312</span></div>
        <div className="flex items-center gap-1"><BarChart2 size={13} /><span className="text-[9px]">2.1k</span></div>
        <Bookmark size={13} />
      </div>
    </div>
  );
}

function LinkedInPostPreview({ imageUrl, caption, hashtags, username }: { imageUrl?: string; caption?: string; hashtags?: string[]; username?: string }) {
  const displayName = username || "Your Brand";
  const shortCaption = caption ? caption.slice(0, 150) : "Your post content here...";
  const hasMore = caption && caption.length > 150;
  return (
    <div className="w-full rounded-[1rem] bg-[#1b1f23] border border-white/10 overflow-hidden" data-testid="li-post-preview">
      <div className="flex items-start gap-2.5 px-3 pt-3 pb-2">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
          {(displayName || "B")[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-[11px] font-semibold leading-tight">{displayName}</p>
          <p className="text-white/40 text-[9px] leading-tight">Your connections · now</p>
          <div className="flex items-center gap-0.5 mt-0.5">
            <Globe size={8} className="text-white/30" />
            <span className="text-white/30 text-[8px]">Anyone</span>
          </div>
        </div>
        <MoreHorizontal size={14} className="text-white/50 flex-shrink-0" />
      </div>

      <div className="px-3 pb-2">
        <p className="text-white text-[10px] leading-snug">
          {shortCaption}
          {hasMore && <span className="text-blue-400 cursor-pointer"> ...see more</span>}
        </p>
        {(hashtags || []).length > 0 && (
          <p className="text-blue-400 text-[9px] mt-1">
            {(hashtags || []).slice(0, 3).map(t => `#${t.replace(/^#/, "")}`).join(" ")}
          </p>
        )}
      </div>

      {imageUrl && (
        <div className="w-full" style={{ aspectRatio: "16/9" }}>
          <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
        </div>
      )}
      {!imageUrl && (
        <div className="w-full bg-gradient-to-br from-blue-900/40 to-indigo-900/40" style={{ aspectRatio: "16/9" }} />
      )}

      <div className="px-3 py-2 space-y-1.5">
        <div className="flex items-center gap-1 text-white/30 text-[9px]">
          <span>👍❤️💡</span>
          <span>342 reactions · 18 comments</span>
        </div>
        <div className="flex items-center justify-around border-t border-white/10 pt-1.5">
          {[
            { icon: <ThumbsUp size={12} />, label: "Like" },
            { icon: <MessageCircle size={12} />, label: "Comment" },
            { icon: <Repeat2 size={12} />, label: "Repost" },
            { icon: <Send size={12} />, label: "Send" },
          ].map(({ icon, label }) => (
            <button key={label} className="flex items-center gap-1 text-white/40 text-[9px] font-medium hover:text-white/70">
              {icon}
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SocialPreviewMockup({
  imageUrl,
  caption,
  platform = "ig_reel",
  hashtags = [],
  username,
  showPlatformToggle = false,
  onPlatformChange,
}: SocialPreviewMockupProps) {
  const PLATFORMS: Platform[] = ["ig_reel", "ig_feed", "fb_feed", "x_post", "li_post"];

  return (
    <div className="flex flex-col gap-2" data-testid="social-preview-mockup">
      {showPlatformToggle && (
        <div className="flex gap-1 flex-wrap">
          {PLATFORMS.map(p => (
            <button
              key={p}
              onClick={() => onPlatformChange?.(p)}
              className={`px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${
                platform === p
                  ? "bg-purple-600 text-white"
                  : "bg-white/10 text-white/60 hover:bg-white/20"
              }`}
              data-testid={`platform-toggle-${p}`}
            >
              {PLATFORM_LABELS[p]}
            </button>
          ))}
        </div>
      )}

      {platform === "ig_reel" && <IgReelPreview imageUrl={imageUrl} caption={caption} hashtags={hashtags} username={username} />}
      {platform === "ig_feed" && <IgFeedPreview imageUrl={imageUrl} caption={caption} hashtags={hashtags} username={username} />}
      {platform === "ig_story" && <IgReelPreview imageUrl={imageUrl} caption={caption} hashtags={hashtags} username={username} />}
      {platform === "fb_feed" && <FbFeedPreview imageUrl={imageUrl} caption={caption} username={username} />}
      {platform === "x_post" && <XPostPreview imageUrl={imageUrl} caption={caption} hashtags={hashtags} username={username} />}
      {platform === "li_post" && <LinkedInPostPreview imageUrl={imageUrl} caption={caption} hashtags={hashtags} username={username} />}
    </div>
  );
}

export type { Platform as SocialPlatform };
